import { Avatar, Box, Button, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ProfileView from '../ProfileView/ProfileView';
import { TbDeviceFloppy, TbExternalLink } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './LivePreview.css'
import { useProfile } from 'context/ProfileContext';
import { getAvatarUrl, getReadableTextOn, isReadableOnDark } from 'utils';
import { useEffect, useMemo, useState } from 'react';
import { STATUS } from 'utils/enums';
import { BRAND_LEFT } from 'utils/constants';
import PreviewStatus from 'Components/PreviewStatus/PreviewStatus';

interface ProfileInfo {
  title?: string;
  description?: string;
}

interface ProfileTheme {
  background?: {
    type?: 'solid' | 'gradient';
    solid?: string;
    gradient?: { left?: string; right?: string; direction?: string };
  };
  text?: { primary?: string; secondary?: string };
}


const LivePreview = () => {
  const { state, deploy, pending, hasChanges, loading } = useProfile();
  const [profileStatus, setProfileStatus] = useState<STATUS>(STATUS.DEFAULT);
  const { t } = useTranslation();
  const { user: { username, userImage, deletedAt } } = useAuth();
  const isInDeletion = !!deletedAt;
  const links = state.currentProfile.links;
  const theme = state.currentProfile.theme as ProfileTheme;
  const info = state.currentProfile.info as ProfileInfo;

  const avatarSrc = useMemo(() => {
    if (!userImage?.fileName) return null;
    return getAvatarUrl(userImage);
  }, [userImage]);

  useEffect(() => {
      if (state.isPublished) {
          if (JSON.stringify(state.lastProfile) !== JSON.stringify(state.currentProfile)) {
            setProfileStatus(STATUS.DRAFT);
          } else {
            setProfileStatus(STATUS.PUBLISHED);
          }
      } else {
          setProfileStatus(STATUS.NOT_PUBLISHED)
      }
  }, [state]);

  const linkProps = !loading && !hasChanges && state.isPublished
    ? { component: Link, to: `/${username}` }
    : {};

  const userColor = theme.background?.gradient?.left ?? BRAND_LEFT;
  const isViewProfile = !hasChanges && state.isPublished;
  const isActionButton = !isViewProfile;
  // Only paint the action button with the user's identity when it's bright
  // enough to stand on a dark surface. Otherwise let MUI's default primary
  // (which is designed for contrast) carry the visibility.
  const useUserColor = isReadableOnDark(userColor);
  // When we DO paint with userColor, pick the text shade that contrasts with it
  // — bright themes (pink, neon green) need black text, dark-but-passing themes
  // (Sunset coral, Ocean blue) need white text.
  const userColorText = getReadableTextOn(userColor);
  const buttonSx = !loading && !pending && isActionButton
    ? {
        ...(useUserColor && {
          backgroundColor: userColor,
          color: userColorText,
          borderColor: 'transparent',
          '&:hover': {
            backgroundColor: userColor,
            color: userColorText,
            filter: 'brightness(1.15)',
            borderColor: 'transparent',
          },
        }),
        ...(hasChanges && {
          animation: 'pulse-button 2s ease-in-out infinite',
          '@keyframes pulse-button': {
            '0%, 100%': { filter: 'drop-shadow(0 0 0px rgba(255,255,255,0))' },
            '50%': { filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.35))' },
          },
        }),
      }
    : undefined;

  return (
    <Paper className='live-preview' variant='outlined' elevation={0} sx={{ p: 2 }}>
      <Box className='live-preview-header'>
        {loading
          ? <Skeleton variant="rounded" width={160} height={14} />
          : <PreviewStatus pending={pending} publishDate={state.publishDate} profileStatus={profileStatus} t={t} />
        }
        <Tooltip title={!loading && isInDeletion && isActionButton ? t('dashboard.main.deletion-alert.guard-tooltip') : ''}>
          <span>
            <Button
              variant={isActionButton ? 'contained' : 'outlined'}
              size="small"
              disabled={loading || pending || (isActionButton && isInDeletion)}
              sx={buttonSx}
              onClick={isViewProfile ? undefined : deploy}
              {...linkProps as object}
            >
              {loading ? (
                <div className='btn-text-container'><Skeleton width={80} height={13} /></div>
              ) : (
                <>
                  <span className='live-preview-btn-text-long'>
                    {isViewProfile ? t('profile.preview.status.view-profile') : t('profile.save-changes')}
                  </span>
                  <span className='live-preview-btn-text-short'>
                    {isViewProfile ? t('profile.preview.view-short') : t('profile.preview.save-short')}
                  </span>
                  <span className='live-preview-btn-icon' aria-hidden="true">
                    {isViewProfile ? <TbExternalLink size={16} /> : <TbDeviceFloppy size={16} />}
                  </span>
                </>
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <div className='phone-frame-wrapper'>
        <div className='phone-frame'>
          <ProfileView
            className={`live-preview-links ${username == null && links.length === 0 ? 'empty-preview' : ''}`}
            links={links}
            theme={theme}
            info={info}
            username={username}
            origin='preview'
            loading={loading}
            avatar={avatarSrc ? <Avatar src={avatarSrc} /> : undefined}
          />
          <div className='phone-home-bar'>
            <div className='phone-home-bar-indicator'>
              <Typography variant='caption' sx={{ color: 'text.primary', opacity: 0.7 }}>linkerto.com</Typography>
            </div>
          </div>
        </div>
      </div>
    </Paper>
  )
}

export default LivePreview
